<template>
    <fragment v-if="model">
        <validation-provider v-if="type === Types.select" ref="validator" :rules="{match: {schema: schema}, ...rules}" slim v-slot="{dirty, invalid}">
            <div class="ui input" :class="{'left icon': icon, 'right labeled': $config.get(`${resource}.label`, false), error: invalid && dirty}">
                <i v-if="icon" :class="[icon, 'icon']"></i>
                <ui-dropdown ref="input" v-model="value"
                    :class="{error: dirty && invalid, disabled}"
                    :placeholder="$t(`${resource}.placeholder`)"
                    :variant="variant"
                    :disabled="disabled || isCalculated"
                    v-on="$listeners"
                    @focusout="onFocusoutInput"
                >
                    <template v-slot:text="text">
                        <i v-if="$config.has(`${resource}.items.${text}.icon`)"
                            :class="$config.get(`${resource}.items.${text}.icon`, undefined)"></i>
                        {{ $t(`${resource}.items.${text.value}.label`) }}
                    </template>
                    <template v-slot:items="items">
                        <div v-for="(item, index) in schema.enum.filter(Boolean)" @click.self="items.select(item)" 
                            :key="index" :class="{item: true, active: items.active === item, selected: items.active === item}">
                            <i v-if="$config.has(`${resource}.items.${item}.icon`)"
                                :class="$config.get(`${resource}.items.${item}.icon`, undefined)"></i>
                            {{ $t(`${resource}.items.${item}.label`) }}
                        </div>
                    </template>
                </ui-dropdown>
                <slot></slot>
            </div>
        </validation-provider>
        <validation-provider v-else-if="type === Types.search" ref="validator" :rules="rules" slim>
            <ui-geocode ref="input" v-model="value" :placeholder="$t(`${resource}.placeholder`)" @focusout="onFocusoutInput">
            </ui-geocode>
        </validation-provider>
        <validation-provider v-else-if="type === Types.check" class="ui inverted toggle checkbox" ref="validator" :rules="rules" tag="div">
            <input type="checkbox" ref="input" v-model="value" v-on="$listeners" @change="onChangeInput" />
            <label>{{ $t(`${resource}.placeholder`) }}</label>
        </validation-provider>
        <validation-provider v-else-if="type === Types.input" ref="validator" :rules="{match: {schema: schema}, ...rules}" slim v-slot="{dirty, invalid}">
            <div class="ui input" :class="{'left icon': icon, 'right labeled': $config.get(`${resource}.label`, false), error: invalid && dirty}">
                <i v-if="icon" :class="[icon, 'icon']"></i>
                <input ref="input" type="text" v-model="value"
                    :placeholder="$t(`${resource}.placeholder`)" :disabled="disabled"
                    v-on="$listeners" @focusout="onFocusoutInput"
                />
                <div v-if="$config.get(`${resource}.label`, false)" class="ui basic label">
                    {{ $config.get(`${resource}.label`, '').replace('{currency}', currency) }}
                </div>
                <slot></slot>
            </div>
        </validation-provider>
    </fragment>
</template>

<script>
import UiDropdown from './UiDropdown.vue'
import UiGeocode from './UiGeocode.vue'

import { ValidationProvider } from 'vee-validate'

const Types = Object.freeze({
    search: Symbol('Search'),
    select: Symbol('Select'),
    input: Symbol('Input'),
    check: Symbol('Check')
})

export default {
    components: {
        UiDropdown,
        UiGeocode,
        ValidationProvider
    },
    props: {
        model: {
            type: Object,
            required: true,
            default: null
        },
        variant: {
            type: String,
            default: null
        },
        icon: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        rules: {
            type: Object,
            default() {
                return {}
            }
        },
        isCalculated: {
            type: Boolean,
            default: false
        },
        currency: {
            type: String,
        },
    },
    created() {
        this.Types = Types

        if (this.type === Types.check && this.value === null) {
            // Check box: Null means false
            this.$emit('input', false)
            this.update()
        } else if (this.type === Types.select && !this.schema.enum.filter(Boolean).includes(this.value)) {
            // Select: Illegal value means null
            this.$emit('input', null);
            this.update();
        }
    },
    watch: {
        model() {
            if (this.type === Types.check && this.value === null) {
                // Check box: Null means false
                this.$emit('input', false)
                this.update()
            } else if (this.type === Types.select && !this.schema.enum.filter(Boolean).includes(this.value)) {
                // Select: Illegal value means null
                this.$emit('input', null);
                this.update();
            }
        }
    },
    data() {
        return {
            record: null,
            errors: []
        }
    },
    methods: {
        onFocusoutInput() {
            this.update()
        },
        onChangeInput() {
            this.update()
        },
        async update() {
            if (!this.$refs.validator) {
                return
            }

            const result = await this.$refs.validator.validate()

            this.$refs.validator.applyResult(result)

            if ('match' in result.failedRules || !this.$refs.validator.flags.dirty) {
                return
            }

            this.$queue.enqueue(async () => {
                await this.$store.dispatch('patch', {
                    scope: this.model.scope,
                    attributes: [this.model.name],
                    ...(this.type === Types.search ? {
                        extension:  {...this.$refs.input.coordinates}
                    } : undefined)
                })

                if (result.failedRules.valid) {
                    this.$refs.validator.reset()
                }
            })
        },
        focus() {
            this.$refs.input.focus()
        }
    },
    computed: {
        type() {
            if (this.schema.type === 'object' && this.schema.properties && 'city' in this.schema.properties && 'country' in this.schema.properties) {
                return Types.search
            } else if (this.schema.type === 'string' && this.schema.enum) {
                return Types.select
            } else if (this.schema.type === 'object' && this.schema.enum) {
                return Types.select
            } else if (this.schema.type === 'boolean') {
                return Types.check
            }

            return Types.input
        },
        isWrapped() {
            return this.model.schema.type === 'object' 
                    && this.model.schema.properties?.value 
                    && this.model.schema.properties?.function
        },
        isDouble() {
            return this.schema && this.schema.format == "double"
        },
        isPercent() {
            return this.model.name == "heatingEfficiency"
        },
        resource() {
            return `scopes.${this.model.scope.type}.variables.${this.model.name}`
        },
        schema() {
            return this.isWrapped ? this.model.schema.properties.value : this.model.schema
        },
        value: {
            get() {
                if (this.model === null) {
                    return undefined
                }

                var val = this.isWrapped ? this.model.context[this.model.name].value : this.model.context[this.model.name];

                if (val && this.isPercent) {
                    val = `${Math.round(parseFloat(val) * 100)}`
                }

                if (val && this.isDouble) {
                    val = `${val}`.replace(/\./g, ",")
                }

                if (val == "NaN" || val == "undefined") {
                    val = null;
                }

                return val;
            },  
            set(value) {
                if (this.model === null) {
                    return
                }

                var val = value

                if (val && this.isDouble) {
                    val = parseFloat(val.replace(/,/g, "."))
                    val = isNaN(val) ? null : val
                }

                if (val && this.isPercent) {
                    val = parseFloat(val) / 100
                    val = isNaN(val) ? null : val
                } else if ((typeof value === 'string' || value instanceof String)  
                    && value.slice(-1) == ","
                    && `${val}`.slice(-1) != ","
                    ) {
                    val = `${val},`
                }

                if (/^[0-9]+,0*$/.test(value)) {
                    val = value
                }

                if (this.isWrapped) {
                    this.model.context[this.model.name].value = val
                    this.model.context[this.model.name].function = null
                } else {
                    this.model.context[this.model.name] = val
                }
            }
        }
    }
}
</script>